import * as React from 'react';
import * as styled from './styled';
import Dropdown, {
  DropdownAlign,
  DropdownProps,
  DropdownSide,
} from 'components/Dropdown';
import {IconRoundPlus} from 'icons';
import {InstanceSelectProps, InstanceSelectVariant} from './types';

/**
 * The select component to pick instance.
 * @param {InstanceSelectProps} props
 * @return {React.ReactElement<InstanceSelectProps>}
 * @constructor
 */
function InstanceSelect(
    props: InstanceSelectProps,
): React.ReactElement<InstanceSelectProps> {
  const [
    inputValue, setInputValue,
  ] = React.useState<React.HTMLAttributes<HTMLInputElement>['defaultValue']>(
      props.input?.defaultValue,
  );

  const [
    focused, setFocused,
  ] = React.useState<boolean>(false);

  /**
   * Render header.
   * @return {JSX.Element}
   */
  function renderHeader(open: () => void, close: () => void): JSX.Element {
    /**
     * Render input.
     * @return {JSX.Element}
     */
    function renderInput(): JSX.Element {
      /**
       * The change event handler.
       * @param {React.ChangeEvent<HTMLInputElement>} event
       */
      function handleChange(event: React.ChangeEvent<HTMLInputElement>): void {
        setInputValue(event.target.value);
        props.input?.onChange && (
          props.input?.onChange(event)
        );
      }

      /**
       * The focus event handler.
       * @param {React.FocusEvent<HTMLInputElement>} event
       */
      function handleFocus(event: React.FocusEvent<HTMLInputElement>): void {
        open();
        setFocused(true);
        props.input?.onFocus && (
          props.input?.onFocus(event)
        );
      }

      /**
       * The blur event handler.
       * @param {React.FocusEvent<HTMLInputElement>} event
       */
      function handleBlur(event: React.FocusEvent<HTMLInputElement>): void {
        setFocused(false);
        props.input?.onBlur && (
          props.input?.onBlur(event)
        );
      }

      return (
        <styled.Input
          {...props.input}
          value={inputValue}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      );
    }

    /**
     * Render add control.
     * @return {JSX.Element | undefined}
     */
    function renderAddControl(): JSX.Element | undefined {
      /**
       * The click event handler.
       */
      function handleClick(): void {
        props.onAddClick && (
          props.onAddClick(inputValue)
        );
      }

      return props.onAddClick ? (
        <styled.AddControl type="button" onClick={handleClick}>
          <IconRoundPlus />
        </styled.AddControl>
      ) : undefined;
    }

    return (
      <styled.Header>
        {renderInput()}
        {renderAddControl()}
      </styled.Header>
    );
  }

  /**
   * Render dropdown.
   * @param {DropdownProps["trigger"]} trigger
   * @return {JSX.Element}
   */
  function renderDropdown(trigger: DropdownProps['trigger']): JSX.Element {
    return (
      <Dropdown
        trigger={trigger}
        side={DropdownSide.Bottom}
        align={DropdownAlign.Center}
        gap={6}
      >
        {props.children}
      </Dropdown>
    );
  }

  return (
    <styled.Wrapper variant={props.variant} focused={focused}>
      {renderDropdown(renderHeader)}
    </styled.Wrapper>
  );
}

InstanceSelect.defaultProps = {
  variant: InstanceSelectVariant.Primary,
};

export default InstanceSelect;
